"use client";

import { useRef, useState } from "react";
import type {
  GalleryDevice,
  GalleryImageData,
  GalleryMouseState,
} from "../../blocks/image-gallery/image-gallery.types";
import { Button } from "../../elements/button/button";
import { GalleryImage } from "../../elements/gallery-image/gallery-image";
import { Text } from "../../elements/text/text";
import { IconArrowLeft, IconArrowRight, IconMaximize } from "../../icons";
import { cn } from "../../lib/utils";

const SWIPE_THRESHOLD = 50;

export interface BigImageAreaProps {
  /** Array of gallery images */
  images: GalleryImageData[];

  /** Currently active image index */
  activeIndex: number;

  /** Callback when image changes */
  onImageChange: (index: number) => void;

  /** Callback to open overlay */
  onOverlayOpen: () => void;

  /** Mouse state matching Figma variant */
  mouse?: GalleryMouseState;

  /** Device type matching Figma variant */
  device?: GalleryDevice;

  /** Microcopies for text content */
  microcopies: {
    viewGallery: string;
    previous: string;
    next: string;
    imageCounter: string;
  };

  /** Show navigation arrows (auto-hide on mobile) */
  showNavigationArrows?: boolean;

  /** Additional CSS classes */
  className?: string;
}

export function BigImageArea(props: BigImageAreaProps) {
  const [isHovered, setIsHovered] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const canGoPrevious = props.activeIndex > 0;
  const canGoNext = props.activeIndex < props.images.length - 1;
  const hasMultipleImages = props.images.length > 1;

  const currentImage = props.images[props.activeIndex];
  const isMobile = props.device === "Mobile";

  // Show arrows only when: multiple images, not mobile, on hover
  const showArrows =
    hasMultipleImages && props.showNavigationArrows !== false && !isMobile;
  const arrowsVisible = isHovered || props.mouse === "mouse over";

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || !hasMultipleImages) {
      return;
    }

    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;

    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff > 0 && canGoNext) {
        props.onImageChange(props.activeIndex + 1);
      } else if (diff < 0 && canGoPrevious) {
        props.onImageChange(props.activeIndex - 1);
      }
    }

    touchStartX.current = null;
  };

  return (
    <div className={cn("flex w-full flex-col gap-4", props.className)}>
      {/*
        Figma "Carousel images > image": a square that fills the gallery
        column at every frame (343 at 375 … 1090 at 2560), never a fixed box.
      */}
      <div
        className="relative aspect-square w-full overflow-hidden bg-muted"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchEnd={handleTouchEnd}
        onTouchStart={handleTouchStart}
      >
        {/* Main Image - Clickable to open overlay */}
        <GalleryImage
          alt={currentImage.alt}
          objectFit="cover"
          onClick={props.onOverlayOpen}
          src={currentImage.src}
        />

        {/* Expand Button (Maximize icon) - Bottom-right per Figma */}
        <Button
          aria-label={props.microcopies.viewGallery}
          className="absolute right-2 bottom-2 shadow-sm"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            props.onOverlayOpen();
          }}
          size="icon"
          variant="secondary"
        >
          <IconMaximize className="!h-4 !w-4" />
        </Button>

        {/* Navigation Arrows - Only visible on hover, completely hidden otherwise */}
        {showArrows && arrowsVisible && (
          <>
            <Button
              aria-label={props.microcopies.previous}
              className="-translate-y-1/2 absolute top-1/2 left-2 hidden shadow-sm md:flex"
              disabled={!canGoPrevious}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                props.onImageChange(props.activeIndex - 1);
              }}
              size="icon"
              variant="outline"
            >
              <IconArrowLeft className="!h-4 !w-4" />
            </Button>

            <Button
              aria-label={props.microcopies.next}
              className="-translate-y-1/2 absolute top-1/2 right-2 hidden shadow-sm md:flex"
              disabled={!canGoNext}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                props.onImageChange(props.activeIndex + 1);
              }}
              size="icon"
              variant="outline"
            >
              <IconArrowRight className="!h-4 !w-4" />
            </Button>
          </>
        )}
      </div>

      {/* Figma "controls > counter": left-aligned under the image, 16px inset */}
      <div className="flex w-full px-4">
        <div className="flex items-center gap-2 text-base text-foreground">
          <Text variant="body-md-bold">{props.activeIndex + 1}</Text>
          <Text>/</Text>
          <Text>{props.images.length}</Text>
        </div>
      </div>

      {/* Screen Reader Live Region */}
      <div aria-atomic="true" aria-live="polite" className="sr-only">
        {props.microcopies.imageCounter
          .replace("{current}", String(props.activeIndex + 1))
          .replace("{total}", String(props.images.length))}
      </div>
    </div>
  );
}
